import { useState } from 'react';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { EnterButton } from '@backrs_ui/grow-ui/src/components/Lubav/EnterButton';

import {
  Box,
  FormControlLabel,
  Radio,
  RadioGroup,
  SxProps,
  Theme,
  Typography,
} from '@mui/material';

import { SkipChatButton } from './SkipChatButton';

type DescribedSelectChatQuestionProps = Readonly<{
  idPrefix?: string;
  options: readonly string[];
  optionDescriptions?: Record<string, string>;
  defaultValue?: string;
  required: boolean;
  isTyping: boolean;
  onSubmit: (selected: string) => void;
  onSkip: () => void;
}>;

export const DescribedSelectChatQuestion = ({
  idPrefix = 'described-select-chat-question',
  options,
  optionDescriptions,
  defaultValue,
  required,
  isTyping,
  onSubmit,
  onSkip,
}: DescribedSelectChatQuestionProps) => {
  // A preselection the option list doesn't contain would leave nothing checked while
  // still enabling Submit.
  const [selected, setSelected] = useState(
    defaultValue && options.includes(defaultValue) ? defaultValue : '',
  );

  const isDisabled = isTyping || selected === '';

  return (
    <Box sx={containerSx}>
      <RadioGroup
        id={`${idPrefix}-group`}
        value={selected}
        onChange={(event) => setSelected(event.target.value)}
        sx={groupSx}
      >
        {options.map((option) => (
          <FormControlLabel
            key={option}
            id={`${idPrefix}-option-${slugify(option)}`}
            value={option}
            checked={option === selected}
            disabled={isTyping}
            control={<Radio id={`${idPrefix}-radio-${slugify(option)}`} />}
            label={
              <Box sx={labelSx}>
                <Typography variant="text-sm-semibold">{option}</Typography>
                {optionDescriptions?.[option] && (
                  <Typography variant="text-sm-regular" sx={descriptionSx}>
                    {optionDescriptions[option]}
                  </Typography>
                )}
              </Box>
            }
          />
        ))}
      </RadioGroup>
      <Box sx={actionsRowSx}>
        <EnterButton
          id={`${idPrefix}-submit`}
          onClick={() => onSubmit(selected)}
          label={appStrings.modules.solutionSessions.questions.submit}
          showEnterHint
          disabled={isDisabled}
        />
        {required ? (
          <Box />
        ) : (
          <SkipChatButton id={`${idPrefix}-skip`} disabled={isTyping} onSkip={onSkip} />
        )}
      </Box>
    </Box>
  );
};

const slugify = (option: string) => option.toLowerCase().replace(/\s+/g, '-');

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-md'],
  width: '100%',
});

const groupSx: SxProps<Theme> = ({ backrs }) => ({
  gap: backrs.spacing['spacing-sm'],
});

const labelSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xxs'],
});

const descriptionSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-secondary'],
});

const actionsRowSx: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  width: '100%',
};
